define([
    '../Util/defaultValue',
    '../Util/defined'
], function (
    defaultValue,
    defined
) {
    'use strict';

    /**
     * 系统监控面板，可独立集成到任何网页中
     * @alias Monitor
     * @constructor
     * @param {Object} [options] 配置选项
     * @param {String} [options.containerId] 容器div的ID，如果不提供会自动创建
     * @param {Object} [options.mapInstance] 地图实例，用于获取节点信息
     * @param {String} [options.position='bottom-right'] 面板位置: 'bottom-right', 'bottom-left', 'top-right', 'top-left'
     * @param {Boolean} [options.showNodes=true] 是否显示节点监控
     * @param {Number} [options.maxMemory=4095] 最大内存限制(MB)
     * @param {String} [options.theme='dark'] 主题: 'dark', 'light'
     * @param {Boolean} [options.autoCreate=true] 当containerId不存在时是否自动创建容器
     * 
     * @example
     * // 自动创建容器（最简用法）
     * const monitor = new CooWasm.Monitor();
     * 
     * // 指定容器
     * const monitor = new CooWasm.Monitor({
     *     containerId: 'monitor-container',
     *     mapInstance: map
     * });
     * 
     * // 自定义配置
     * const monitor = new CooWasm.Monitor({
     *     position: 'top-left',
     *     showNodes: false,
     *     theme: 'light'
     * });
     */
    class Monitor {
        constructor(options) {
            this.options = defaultValue(options, {});
            this.containerId = this.options.containerId;
            this.mapInstance = this.options.mapInstance || null;
            this.position = defaultValue(this.options.position, 'bottom-right');
            this.showNodes = defaultValue(this.options.showNodes, true);
            this.maxMemory = defaultValue(this.options.maxMemory, 4095);
            this.theme = defaultValue(this.options.theme, 'dark');
            this.autoCreate = defaultValue(this.options.autoCreate, true);
            this.autoCreatedContainer = false; // 标记是否是自动创建的容器

            // 如果没有提供containerId，自动创建一个
            if (!this.containerId) {
                if (this.autoCreate) {
                    this.containerId = this.createAutoContainer();
                    this.autoCreatedContainer = true;
                } else {
                    throw new Error('Monitor: containerId is required when autoCreate is false');
                }
            }

            // 监控数据
            this.memoryHistory = [];
            this.maxHistoryLength = 86400; // 24小时
            this.chart = null;
            this.isCollapsed = false;
            this.currentMemory = 0;
            this.currentDataRequest = 0;
            this.currentTotalNodes = 0;
            this.currentActiveNodes = 0;
            
            // 帧率监控数据
            this.currentFPS = 0;
            this.frameTimestamps = []; // 存储最近的帧时间戳
            this.fpsUpdateInterval = null;

            this.init();
            this.startFPSMonitoring();
        }

        createAutoContainer() {
            // 生成唯一的容器ID
            const containerId = `monitor-auto-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            
            // 创建容器div
            const container = document.createElement('div');
            container.id = containerId;
            container.style.cssText = `
                position: fixed;
                z-index: 10000;
                pointer-events: none;
            `;
            
            // 添加到body
            document.body.appendChild(container);
            
            console.log(`Monitor: 自动创建容器 ${containerId}`);
            return containerId;
        }

        init() {
            this.createStyles();
            this.createHTML();
            this.initChart();
            this.bindEvents();
            this.loadHistoryFromStorage();
            this.startMonitoring();
        }

        createStyles() {
            const styleId = 'monitor-styles';
            if (document.getElementById(styleId)) return;

            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = this.getCSS();
            document.head.appendChild(style);
        }

        getCSS() {
            const themeColors = this.theme === 'dark' ? {
                bg: 'rgba(0, 0, 0, 0.85)',
                border: '#333',
                text: '#fff',
                headerBg: 'rgba(255, 255, 255, 0.1)',
                btnHover: 'rgba(255, 255, 255, 0.2)',
                chartBg: 'rgba(255, 255, 255, 0.05)',
                statLabel: '#bbb',
                statValue: '#4CAF50'
            } : {
                bg: 'rgba(255, 255, 255, 0.95)',
                border: '#ddd',
                text: '#333',
                headerBg: 'rgba(0, 0, 0, 0.1)',
                btnHover: 'rgba(0, 0, 0, 0.1)',
                chartBg: 'rgba(0, 0, 0, 0.05)',
                statLabel: '#666',
                statValue: '#4CAF50'
            };

            const positionStyles = this.getPositionStyles();

            return `
                .monitor-panel {
                    position: fixed;
                    ${positionStyles}
                    width: 320px;
                    background: ${themeColors.bg};
                    border: 1px solid ${themeColors.border};
                    border-radius: 8px;
                    color: ${themeColors.text};
                    font-family: 'Courier New', monospace;
                    font-size: 12px;
                    z-index: 1000;
                    backdrop-filter: blur(5px);
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
                }

                .monitor-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 12px;
                    background: ${themeColors.headerBg};
                    border-bottom: 1px solid ${themeColors.border};
                    border-radius: 7px 7px 0 0;
                }

                .monitor-title {
                    font-weight: bold;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }

                .monitor-fps {
                    font-size: 11px;
                    color: ${themeColors.statValue};
                    font-weight: normal;
                }

                .monitor-toggle {
                    background: none;
                    border: none;
                    color: ${themeColors.text};
                    cursor: pointer;
                    font-size: 16px;
                    padding: 0;
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .monitor-toggle:hover {
                    background: ${themeColors.btnHover};
                    border-radius: 3px;
                }

                .monitor-content {
                    position: relative;
                    display: block;
                    padding: 12px;
                    overflow: hidden;
                }

                .monitor-stats {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid ${themeColors.border};
                    z-index: 1;
                }

                .monitor-stat-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 8px;
                }

                .monitor-stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex: 1;
                    text-align: center;
                }

                .monitor-stat-label {
                    color: ${themeColors.statLabel};
                    font-size: 10px;
                    margin-bottom: 2px;
                }

                .monitor-stat-value {
                    color: ${themeColors.statValue};
                    font-weight: bold;
                    font-size: 12px;
                }

                .monitor-chart {
                    position: relative;
                    display: block;
                    width: 100%;
                    height: 140px;
                    background: ${themeColors.chartBg};
                    border: 1px solid ${themeColors.border};
                    border-radius: 4px;
                    margin-top: 10px;
                    margin-bottom: 12px;
                    clear: both;
                }

                .monitor-controls {
                    position: relative;
                    display: flex;
                    gap: 8px;
                    margin-top: 8px;
                    clear: both;
                }

                .monitor-btn {
                    flex: 1;
                    padding: 6px 8px;
                    background: ${themeColors.headerBg};
                    border: 1px solid ${themeColors.border};
                    border-radius: 4px;
                    color: ${themeColors.text};
                    cursor: pointer;
                    font-size: 11px;
                }

                .monitor-btn:hover {
                    background: ${themeColors.btnHover};
                }

                .monitor-panel.collapsed .monitor-content {
                    display: none;
                }

                .monitor-panel.collapsed {
                    width: auto;
                }
            `;
        }

        getPositionStyles() {
            switch (this.position) {
                case 'top-left':
                    return 'top: 20px; left: 20px;';
                case 'top-right':
                    return 'top: 20px; right: 20px;';
                case 'bottom-left':
                    return 'bottom: 20px; left: 20px;';
                case 'bottom-right':
                default:
                    return 'bottom: 20px; right: 20px;';
            }
        }

        createHTML() {
            const container = document.getElementById(this.containerId);
            if (!container) {
                throw new Error(`Monitor: Container with id '${this.containerId}' not found`);
            }

            // 如果是自动创建的容器，设置pointer-events为auto以允许交互
            if (this.autoCreatedContainer) {
                container.style.pointerEvents = 'auto';
            }

            const nodeStats = this.showNodes ? `
                <div class="monitor-stat-row">
                    <div class="monitor-stat">
                        <span class="monitor-stat-label">请求节点</span>
                        <span id="monitor-data-request" class="monitor-stat-value">0</span>
                    </div>
                    <div class="monitor-stat">
                        <span class="monitor-stat-label">调度节点</span>
                        <span id="monitor-total-nodes" class="monitor-stat-value">0</span>
                    </div>
                    <div class="monitor-stat">
                        <span class="monitor-stat-label">激活节点</span>
                        <span id="monitor-active-nodes" class="monitor-stat-value">0</span>
                    </div>
                </div>
            ` : '';

            container.innerHTML = `
                <div class="monitor-panel">
                    <div class="monitor-header">
                        <span class="monitor-title">
                            系统监控
                            <span id="monitor-fps" class="monitor-fps">FPS: --</span>
                        </span>
                        <button id="monitor-toggle" class="monitor-toggle">-</button>
                    </div>
                    <div class="monitor-content">
                        <div class="monitor-stats">
                            <div class="monitor-stat-row">
                                <div class="monitor-stat">
                                    <span class="monitor-stat-label">已用</span>
                                    <span id="monitor-memory-used" class="monitor-stat-value">0 MB</span>
                                </div>
                                <div class="monitor-stat">
                                    <span class="monitor-stat-label">总计</span>
                                    <span class="monitor-stat-value">${this.maxMemory} MB</span>
                                </div>
                                <div class="monitor-stat">
                                    <span class="monitor-stat-label">使用率</span>
                                    <span id="monitor-memory-percentage" class="monitor-stat-value">0%</span>
                                </div>
                            </div>
                            ${nodeStats}
                        </div>
                        <canvas id="monitor-chart" class="monitor-chart" width="320" height="140"></canvas>
                        <div class="monitor-controls">
                            <button id="monitor-clear-history" class="monitor-btn">清除历史</button>
                            <button id="monitor-export-data" class="monitor-btn">导出数据</button>
                        </div>
                    </div>
                </div>
            `;
        }

        initChart() {
            const canvas = document.getElementById('monitor-chart');
            if (canvas) {
                this.chart = canvas.getContext('2d');
                this.chart.imageSmoothingEnabled = true;
            }
        }

        bindEvents() {
            // 折叠/展开面板
            const toggle = document.getElementById('monitor-toggle');
            if (toggle) {
                toggle.addEventListener('click', () => this.togglePanel());
            }

            // 清除历史
            const clearBtn = document.getElementById('monitor-clear-history');
            if (clearBtn) {
                clearBtn.addEventListener('click', () => this.clearHistory());
            }

            // 导出数据
            const exportBtn = document.getElementById('monitor-export-data');
            if (exportBtn) {
                exportBtn.addEventListener('click', () => this.exportData());
            }
        }

        togglePanel() {
            const panel = document.querySelector(`#${this.containerId} .monitor-panel`);
            const toggle = document.getElementById('monitor-toggle');
            
            this.isCollapsed = !this.isCollapsed;
            
            if (this.isCollapsed) {
                panel.classList.add('collapsed');
                toggle.textContent = '+';
            } else {
                panel.classList.remove('collapsed');
                toggle.textContent = '-';
                this.drawChart();
            }
        }

        clearHistory() {
            this.memoryHistory = [];
            localStorage.removeItem('monitorHistory');
            this.drawChart();
            console.log('Monitor: 监控历史已清除');
        }

        exportData() {
            const data = {
                timestamp: new Date().toISOString(),
                maxMemory: this.maxMemory,
                dataPoints: this.memoryHistory.length,
                samplingRate: "1 second",
                showNodes: this.showNodes,
                currentState: {
                    memory_mb: this.currentMemory,
                    dataRequest: this.currentDataRequest,
                    totalNodes: this.currentTotalNodes,
                    activeNodes: this.currentActiveNodes
                },
                history: this.memoryHistory.map(item => ({
                    timestamp: new Date(item.time).toISOString(),
                    memory_mb: item.memory,
                    usage_percentage: ((item.memory / this.maxMemory) * 100).toFixed(2),
                    data_request: item.dataRequest || 0,
                    total_nodes: item.totalNodes || 0,
                    active_nodes: item.activeNodes || 0
                }))
            };
            
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `monitor-data-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            console.log(`Monitor: 数据已导出 ${this.memoryHistory.length} 个数据点`);
        }

        startMonitoring() {
            // 每秒更新一次数据
            this.intervalId = setInterval(() => {
                this.updateData();
                this.saveToHistory();
            }, 1000);

            // 每10分钟保存一次到本地存储
            this.storageIntervalId = setInterval(() => {
                this.cleanupOldData();
                this.saveHistoryToStorage();
            }, 600000);

            // 立即执行一次
            this.updateData();
            this.saveToHistory();
        }

        startFPSMonitoring() {
            // 使用requestAnimationFrame来准确计算帧率
            this.frameTimestamps = [];
            
            const recordFrame = () => {
                const now = performance.now();
                this.frameTimestamps.push(now);
                
                // 只保留最近1秒内的帧时间戳
                const oneSecondAgo = now - 1000;
                this.frameTimestamps = this.frameTimestamps.filter(timestamp => timestamp > oneSecondAgo);
                
                // 继续下一帧
                requestAnimationFrame(recordFrame);
            };
            
            // 开始记录帧
            requestAnimationFrame(recordFrame);
            
            // 每200ms更新一次FPS显示
            this.fpsUpdateInterval = setInterval(() => {
                this.updateFPSDisplay();
            }, 200);
        }

        updateFPSDisplay() {
            try {
                // 计算最近1秒内的帧数
                const now = performance.now();
                const oneSecondAgo = now - 1000;
                const recentFrames = this.frameTimestamps.filter(timestamp => timestamp > oneSecondAgo);
                
                // FPS就是最近1秒内的帧数
                this.currentFPS = recentFrames.length;
                
                // 更新显示
                const fpsElement = document.getElementById('monitor-fps');
                if (fpsElement) {
                    fpsElement.textContent = `FPS: ${this.currentFPS}`;
                    
                    // 根据帧率设置颜色
                    let color = '#4CAF50'; // 绿色 (好)
                    if (this.currentFPS < 30) {
                        color = '#F44336'; // 红色 (差)
                    } else if (this.currentFPS < 50) {
                        color = '#FF9800'; // 橙色 (一般)
                    }
                    fpsElement.style.color = color;
                }
                
            } catch (error) {
                console.warn('Monitor: 更新帧率显示失败:', error);
            }
        }

        updateData() {
            try {
                // 获取内存使用情况
                let usedMemory = 0;
                let isValidMemoryValue = false;
                
                // 优先使用Module方式获取内存
                if (typeof Module !== 'undefined' && Module.HEAP8) {
                    const usedMemory_bytes = Module._malloc(0);
                    const usedMemory_temp = usedMemory_bytes / (1024 * 1024);
                    
                    if (usedMemory_temp >= 1000) {
                        usedMemory = usedMemory_temp;
                        isValidMemoryValue = true;
                    } else if (this.currentMemory >= 1000) {
                        usedMemory = this.currentMemory;
                        isValidMemoryValue = true;
                    }
                }
                // 备用方法：通过地图实例获取
                else if (this.mapInstance && typeof this.mapInstance.memoryUsage === 'function') {
                    const memoryUsage = this.mapInstance.memoryUsage();
                    if (typeof memoryUsage === 'number') {
                        usedMemory = memoryUsage;
                        isValidMemoryValue = true;
                    }
                }

                // 获取节点信息（如果有地图实例且启用节点监控）
                if (this.showNodes && this.mapInstance) {
                    if (typeof this.mapInstance.getDataRequest === 'function') {
                        this.currentDataRequest = this.mapInstance.getDataRequest();
                    }
                    if (typeof this.mapInstance.getNodes === 'function') {
                        this.currentTotalNodes = this.mapInstance.getNodes();
                    }
                    if (typeof this.mapInstance.getActiveNodes === 'function') {
                        this.currentActiveNodes = this.mapInstance.getActiveNodes();
                    }
                }

                // 更新显示
                if (isValidMemoryValue) {
                    this.updateDisplay(usedMemory);
                    this.currentMemory = usedMemory;
                }
            } catch (error) {
                console.warn('Monitor: 获取数据失败:', error);
            }
        }

        updateDisplay(usedMemory) {
            const percentage = ((usedMemory / this.maxMemory) * 100).toFixed(1);
            
            // 更新内存显示
            const usedElement = document.getElementById('monitor-memory-used');
            const percentageElement = document.getElementById('monitor-memory-percentage');
            
            if (usedElement) usedElement.textContent = `${usedMemory.toFixed(1)} MB`;
            if (percentageElement) percentageElement.textContent = `${percentage}%`;

            // 根据使用率设置颜色
            const color = percentage > 80 ? '#F44336' : percentage > 60 ? '#FF9800' : '#4CAF50';
            if (usedElement) usedElement.style.color = color;
            if (percentageElement) percentageElement.style.color = color;

            // 更新节点显示
            if (this.showNodes) {
                const dataRequestElement = document.getElementById('monitor-data-request');
                const totalNodesElement = document.getElementById('monitor-total-nodes');
                const activeNodesElement = document.getElementById('monitor-active-nodes');
                
                if (dataRequestElement) dataRequestElement.textContent = this.currentDataRequest.toString();
                if (totalNodesElement) totalNodesElement.textContent = this.currentTotalNodes.toString();
                if (activeNodesElement) activeNodesElement.textContent = this.currentActiveNodes.toString();
            }

            // 更新图表
            if (!this.isCollapsed) {
                this.drawChart();
            }
        }

        saveToHistory() {
            if (this.currentMemory >= 1000) {
                const timestamp = Date.now();
                this.memoryHistory.push({
                    time: timestamp,
                    memory: this.currentMemory,
                    dataRequest: this.currentDataRequest,
                    totalNodes: this.currentTotalNodes,
                    activeNodes: this.currentActiveNodes
                });

                if (this.memoryHistory.length > this.maxHistoryLength) {
                    this.memoryHistory.shift();
                }
            }
        }

        cleanupOldData() {
            const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
            this.memoryHistory = this.memoryHistory.filter(item => item.time > oneDayAgo);
        }

        saveHistoryToStorage() {
            try {
                const sixHoursAgo = Date.now() - (6 * 60 * 60 * 1000);
                const recentHistory = this.memoryHistory.filter(item => item.time > sixHoursAgo);
                localStorage.setItem('monitorHistory', JSON.stringify(recentHistory));
            } catch (error) {
                console.warn('Monitor: 保存历史失败:', error);
            }
        }

        loadHistoryFromStorage() {
            try {
                const stored = localStorage.getItem('monitorHistory');
                if (stored) {
                    this.memoryHistory = JSON.parse(stored);
                    this.cleanupOldData();
                }
            } catch (error) {
                console.warn('Monitor: 加载历史失败:', error);
                this.memoryHistory = [];
            }
        }

        drawChart() {
            if (!this.chart || this.memoryHistory.length === 0) return;

            const canvas = document.getElementById('monitor-chart');
            const width = canvas.width;
            const height = canvas.height;

            this.chart.clearRect(0, 0, width, height);

            let displayData = this.memoryHistory;
            if (displayData.length > 1000) {
                const step = Math.ceil(displayData.length / 1000);
                displayData = displayData.filter((_, index) => index % step === 0);
            }

            const maxValue = Math.max(this.maxMemory * 0.1, Math.max(...displayData.map(item => item.memory)));
            const minTime = displayData[0].time;
            const maxTime = displayData[displayData.length - 1].time;
            const timeRange = maxTime - minTime || 1;

            // 绘制网格线
            this.chart.strokeStyle = this.theme === 'dark' ? '#333' : '#ddd';
            this.chart.lineWidth = 1;
            this.chart.setLineDash([2, 2]);

            // 设置合理的边距，避免重叠
            const leftMargin = 50;  // 为Y轴标签留出更多空间
            const rightMargin = 15;
            const topMargin = 20;   // 增加顶部边距，避免与数值区域重叠
            const bottomMargin = 15;
            const chartWidth = width - leftMargin - rightMargin;
            const chartHeight = height - topMargin - bottomMargin;

            for (let i = 0; i <= 4; i++) {
                const y = topMargin + (chartHeight * i / 4);
                this.chart.beginPath();
                this.chart.moveTo(leftMargin, y);
                this.chart.lineTo(leftMargin + chartWidth, y);
                this.chart.stroke();

                this.chart.fillStyle = this.theme === 'dark' ? '#999' : '#666';
                this.chart.font = '10px Courier New';
                this.chart.textAlign = 'right';
                this.chart.textBaseline = 'middle';
                // Y轴标签放在左边距区域内，避免与曲线重叠
                this.chart.fillText(`${(maxValue * (1 - i / 4)).toFixed(0)}MB`, leftMargin - 5, y);
            }

            // 绘制内存曲线
            this.chart.setLineDash([]);
            this.chart.strokeStyle = '#4CAF50';
            this.chart.lineWidth = 2;
            this.chart.beginPath();

            displayData.forEach((item, index) => {
                const x = leftMargin + ((item.time - minTime) / timeRange) * chartWidth;
                const y = topMargin + (1 - item.memory / maxValue) * chartHeight;

                if (index === 0) {
                    this.chart.moveTo(x, y);
                } else {
                    this.chart.lineTo(x, y);
                }
            });

            this.chart.stroke();

            // 绘制当前值点
            if (this.currentMemory !== undefined) {
                const currentX = leftMargin + chartWidth;
                const currentY = topMargin + (1 - this.currentMemory / maxValue) * chartHeight;
                
                this.chart.fillStyle = '#4CAF50';
                this.chart.beginPath();
                this.chart.arc(currentX - 2, currentY, 3, 0, 2 * Math.PI);
                this.chart.fill();
            }
        }

        /**
         * 设置地图实例
         * @param {Object} mapInstance 地图实例
         */
        setMapInstance(mapInstance) {
            this.mapInstance = mapInstance;
        }

        /**
         * 销毁监控器
         */
        destroy() {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
            if (this.storageIntervalId) {
                clearInterval(this.storageIntervalId);
                this.storageIntervalId = null;
            }
            if (this.fpsUpdateInterval) {
                clearInterval(this.fpsUpdateInterval);
                this.fpsUpdateInterval = null;
            }

            const container = document.getElementById(this.containerId);
            if (container) {
                container.innerHTML = '';
                
                // 如果是自动创建的容器，从DOM中移除
                if (this.autoCreatedContainer) {
                    container.remove();
                }
            }

            console.log('Monitor: 监控器已销毁');
            return null;
        }
    }

    return Monitor;
});
